import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Button, IconButton, Tooltip, Alert, CircularProgress,
  Checkbox, Collapse, Paper, ToggleButton, ToggleButtonGroup,
  Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Sync as SyncIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { getApiError } from '../utils/apiError';
import {
  configSyncService,
  DiffSummaryResponse, TableDiffResponse, RecordDiff,
  ConfigTable, TABLE_LABELS, SyncResultItem, SyncDirection,
  InitializeMappingResponse, ConfigSource, SyncSource,
  IlReconcileReport,
} from '../services/configSyncService';

// Shows the "Initialize Mapping" button (backfills the dev↔prod id mapping from
// natural-key matches). Idempotent and non-destructive, so it is safe to leave on.
const SHOW_INIT_MAPPING = true;

// ── Status styling ────────────────────────────────────────────────────────────

const STATUS_CHIP: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
  dev_only:  { label: 'Dev Only',  color: 'warning' },
  different: { label: 'Different', color: 'error' },
  prod_only: { label: 'Prod Only', color: 'default' },
  identical: { label: 'Identical', color: 'success' },
};

/** What to call the two sides of a diff.
 *
 * The engine calls them dev and prod whatever the source, because that is the
 * shape of the comparison. Israel is neither: it is another system entirely,
 * and labelling its records "Dev Only" invites someone to read them as UK dev
 * records. The data is unchanged — only the words are.
 */
function sideLabel(status: string, source: ConfigSource): string {
  if (source !== 'il') return STATUS_CHIP[status]?.label ?? status;
  if (status === 'dev_only') return 'IL Only';
  if (status === 'prod_only') return 'UK Only';
  return STATUS_CHIP[status]?.label ?? status;
}

const devSideName = (source: ConfigSource) => (source === 'il' ? 'IL Only' : 'Dev Only');
const prodSideName = (source: ConfigSource) => (source === 'il' ? 'UK Only' : 'Prod Only');

const canSync = (status: string, direction: SyncDirection) =>
  direction === 'dev_to_prod'
    ? status === 'dev_only' || status === 'different'
    : status === 'prod_only' || status === 'different';

// The id to send to the sync endpoint depends on direction:
// dev_to_prod uses the dev record id, prod_to_dev uses the prod record id.
const syncIdFor = (record: RecordDiff, direction: SyncDirection): number | undefined =>
  direction === 'dev_to_prod' ? record.dev_record?.id : record.prod_record?.id;

// ── Summary view ──────────────────────────────────────────────────────────────

interface SummaryViewProps {
  summary: DiffSummaryResponse;
  onDrillIn: (table: ConfigTable) => void;
  onRefresh: () => void;
  loading: boolean;
  direction: SyncDirection;
  onDirectionChange: (d: SyncDirection) => void;
  source: ConfigSource;
}

const SummaryView: React.FC<SummaryViewProps> = ({
  summary, onDrillIn, onRefresh, loading, direction, onDirectionChange,
  source,
}) => (
  <Box>
    <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
      <Box>
        <Typography variant="h5" fontWeight={600}>Config Sync</Typography>
        <Typography variant="body2" color="text.secondary">
          <code>{summary.dev_url}</code>
        </Typography>
      </Box>
      <Box display="flex" gap={2} alignItems="center">
        {/* The source is chosen by the tabs above, not here. */}
        {/* Israel is read-only to us, so there is no direction to choose. */}
        {source !== 'il' && (
          <ToggleButtonGroup
            value={direction}
            exclusive
            size="small"
            onChange={(_, val) => { if (val) onDirectionChange(val); }}
          >
            <ToggleButton value="dev_to_prod">Dev → Prod</ToggleButton>
            <ToggleButton value="prod_to_dev">Prod → Dev</ToggleButton>
          </ToggleButtonGroup>
        )}
        <Button
          startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={onRefresh}
          disabled={loading}
          variant="outlined"
        >
          Refresh
        </Button>
      </Box>
    </Box>

    <Paper variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: 'grey.50' }}>
            <TableCell sx={{ fontWeight: 600 }}>Table</TableCell>
            <TableCell align="center" sx={{ fontWeight: 600, color: 'warning.main' }}>{devSideName(source)}</TableCell>
            <TableCell align="center" sx={{ fontWeight: 600, color: 'error.main' }}>Different</TableCell>
            <TableCell align="center" sx={{ fontWeight: 600, color: 'text.secondary' }}>{prodSideName(source)}</TableCell>
            <TableCell align="center" sx={{ fontWeight: 600, color: 'success.main' }}>Identical</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {summary.tables.map((t) => {
            const hasChanges = t.dev_only > 0 || t.different > 0;
            const isError = t.identical === -1;
            return (
              <TableRow
                key={t.table}
                hover
                sx={{ backgroundColor: hasChanges ? 'rgba(255,152,0,0.04)' : undefined }}
              >
                <TableCell sx={{ fontWeight: 500 }}>
                  {TABLE_LABELS[t.table as ConfigTable] ?? t.table}
                </TableCell>
                <TableCell align="center">
                  {isError ? '—' : t.dev_only > 0 ? (
                    <Chip label={t.dev_only} size="small" color="warning" />
                  ) : <Typography variant="body2" color="text.secondary">0</Typography>}
                </TableCell>
                <TableCell align="center">
                  {isError ? '—' : t.different > 0 ? (
                    <Chip label={t.different} size="small" color="error" />
                  ) : <Typography variant="body2" color="text.secondary">0</Typography>}
                </TableCell>
                <TableCell align="center">
                  {isError ? '—' : <Typography variant="body2" color="text.secondary">{t.prod_only}</Typography>}
                </TableCell>
                <TableCell align="center">
                  {isError ? (
                    <Tooltip title={t.error || 'Unknown error'} arrow>
                      <Chip label="Error" size="small" color="default" sx={{ cursor: 'help' }} />
                    </Tooltip>
                  ) : <Typography variant="body2" color="text.secondary">{t.identical}</Typography>}
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    onClick={() => onDrillIn(t.table as ConfigTable)}
                    disabled={isError}
                  >
                    View →
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Paper>
  </Box>
);

// ── Inline word diff ──────────────────────────────────────────────────────────

interface TokenOp { type: 'same' | 'del' | 'ins'; text: string }

// HTML tags are treated as single tokens so attributes don't explode the count.
function tokenize(s: string): string[] {
  return s.match(/<[^>]+>|[^<\s]+|\s+/g) ?? [];
}

function computeOps(aStr: string, bStr: string): TokenOp[] {
  const A = tokenize(aStr);
  const B = tokenize(bStr);
  const m = A.length, n = B.length;

  if (m * n > 500_000) {
    return [{ type: 'del', text: aStr }, { type: 'ins', text: bStr }];
  }

  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = A[i-1] === B[j-1] ? dp[i-1][j-1] + 1 : Math.max(dp[i-1][j], dp[i][j-1]);

  const ops: TokenOp[] = [];
  let i = m, j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && A[i-1] === B[j-1]) {
      ops.push({ type: 'same', text: A[i-1] }); i--; j--;
    } else if (j > 0 && (i === 0 || dp[i][j-1] >= dp[i-1][j])) {
      ops.push({ type: 'ins', text: B[j-1] }); j--;
    } else {
      ops.push({ type: 'del', text: A[i-1] }); i--;
    }
  }
  return ops.reverse();
}

const CONTEXT = 8;

const InlineDiff: React.FC<{ ops: TokenOp[]; side: 'a' | 'b'; highlightColor: string }> = ({ ops, side, highlightColor }) => {
  const sideOps = ops
    .filter(op => op.type === 'same' || (side === 'a' ? op.type === 'del' : op.type === 'ins'))
    .map(op => ({ text: op.text, changed: op.type !== 'same' }));

  const n = sideOps.length;
  const show = new Array(n).fill(false);
  for (let i = 0; i < n; i++) {
    if (sideOps[i].changed) {
      for (let j = Math.max(0, i - CONTEXT); j <= Math.min(n - 1, i + CONTEXT); j++) show[j] = true;
    }
  }

  if (!show.some(Boolean)) return null;

  const nodes: React.ReactNode[] = [];
  let k = 0;
  if (show.indexOf(true) > 0) nodes.push(<span key={k++} style={{ color: '#aaa' }}>…</span>);

  let i = 0;
  while (i < n) {
    if (show[i]) {
      const { text, changed } = sideOps[i];
      nodes.push(changed
        ? <mark key={k++} style={{ backgroundColor: highlightColor, borderRadius: 2, padding: '0 1px' }}>{text}</mark>
        : <span key={k++}>{text}</span>
      );
      i++;
    } else {
      while (i < n && !show[i]) i++;
      nodes.push(<span key={k++} style={{ color: '#aaa' }}> … </span>);
    }
  }

  if (show.lastIndexOf(true) < n - 1) nodes.push(<span key={k++} style={{ color: '#aaa' }}>…</span>);

  return (
    <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: 'monospace', fontSize: 12 }}>
      {nodes}
    </span>
  );
};

// ── Record diff expander ──────────────────────────────────────────────────────

const RecordExpander: React.FC<{ record: RecordDiff; source: ConfigSource }> = ({ record, source }) => {
  const [open, setOpen] = useState(false);
  if (!record.diff_fields.length) return null;

  return (
    <>
      <IconButton size="small" onClick={() => setOpen(!open)}>
        {open ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
      </IconButton>
      <Collapse in={open}>
        <Box
          sx={{
            mt: 1, p: 1.5, borderRadius: 1,
            backgroundColor: 'grey.50', border: '1px solid', borderColor: 'divider',
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Field</TableCell>
                {/* The side a value came from, named for the system it is
                    actually in - "Dev" for an Israeli record is misleading. */}
                <TableCell sx={{ fontWeight: 600, py: 0.5, color: 'primary.main' }}>
                  {source === 'il' ? 'IL' : 'Dev'}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, py: 0.5, color: 'text.secondary' }}>
                  {source === 'il' ? 'UK' : 'Prod'}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {record.diff_fields.map((f) => {
                const devStr = JSON.stringify(record.dev_record?.[f] ?? null, null, 2);
                const prodStr = JSON.stringify(record.prod_record?.[f] ?? null, null, 2);
                const ops = computeOps(devStr, prodStr);
                return (
                  <TableRow key={f}>
                    <TableCell sx={{ py: 0.5, fontFamily: 'monospace', fontSize: 12 }}>{f}</TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      <InlineDiff ops={ops} side="a" highlightColor="rgba(76,175,80,0.4)" />
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      <InlineDiff ops={ops} side="b" highlightColor="rgba(244,67,54,0.4)" />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Collapse>
    </>
  );
};

// ── Table detail view ─────────────────────────────────────────────────────────

type StatusFilter = 'all' | 'dev_only' | 'different' | 'prod_only' | 'identical';

interface TableDetailViewProps {
  table: ConfigTable;
  detail: TableDiffResponse;
  onBack: () => void;
  onRefresh: () => void;
  loading: boolean;
  direction: SyncDirection;
  onDirectionChange: (d: SyncDirection) => void;
  source: ConfigSource;
  /** The data flags that belong in this environment; the users screen starts
   *  filtered to them. */
  defaultDataFlags: string[];
}

const TableDetailView: React.FC<TableDetailViewProps> = ({ table, detail, onBack, onRefresh, loading, direction, onDirectionChange, source, defaultDataFlags }) => {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  // Israeli users carry two things no other table does, and both decide
  // whether a person can be synced at all: whether they are still active in
  // Israel, and which environment their data flag belongs to. Shown and
  // filterable here because the alternative - the feed deciding silently -
  // is how a clinician who owned production meetings came to be missing with
  // no visible reason anywhere on this page.
  const ilUsers = source === 'il' && table === 'users';
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [dataFilter, setDataFilter] = useState<Set<string>>(new Set());

  // Preselect the flags that belong here, so the default view - and a
  // select-all from it - behave exactly as they did before the feed stopped
  // filtering on them.
  useEffect(() => {
    setDataFilter(new Set(defaultDataFlags));
  }, [defaultDataFlags, table]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [syncing, setSyncing] = useState(false);
  const [syncResults, setSyncResults] = useState<Record<string, SyncResultItem>>({});

  /** The Israeli side of a record - the only side that carries these. */
  const ilSide = (r: RecordDiff) => (r.dev_record || {}) as Record<string, any>;
  const dataFlagOf = (r: RecordDiff) =>
    String(ilSide(r).data_access ?? '').toUpperCase();
  const isActiveInIsrael = (r: RecordDiff) => ilSide(r).is_active !== false;

  const filtered = detail.records.filter((r) => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (!ilUsers) return true;
    // A UK-only row has no Israeli side to judge, so these filters cannot
    // speak for it and must not hide it.
    if (!r.dev_record) return true;
    if (activeFilter === 'active' && !isActiveInIsrael(r)) return false;
    if (activeFilter === 'inactive' && isActiveInIsrael(r)) return false;
    if (dataFilter.size > 0 && !dataFilter.has(dataFlagOf(r))) return false;
    return true;
  });

  // Every flag the feed actually returned, so the chips match the data rather
  // than a hard-coded list that goes stale the day Israel adds one.
  const dataFlagsPresent = Array.from(new Set(
    detail.records.filter((r) => r.dev_record).map(dataFlagOf).filter(Boolean)
  )).sort();

  const syncable = filtered.filter((r) => canSync(r.status, direction));
  const allSyncableSelected = syncable.length > 0 && syncable.every((r) => selected.has(r.natural_key));

  const toggleAll = () => {
    if (allSyncableSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(syncable.map((r) => r.natural_key)));
    }
  };

  const toggleOne = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const doSync = useCallback(async (records: RecordDiff[]) => {
    const ids = records
      .map((r) => syncIdFor(r, direction))
      .filter((id): id is number => id != null);
    if (ids.length === 0) {
      toast.error('No syncable record ids found');
      return;
    }
    setSyncing(true);
    try {
      const res = await configSyncService.syncRecords({ table, ids, direction, source });
      const ok = res.results.filter((r) => r.success).length;
      const fail = res.results.filter((r) => !r.success).length;
      if (fail === 0) toast.success(`Synced ${ok} record${ok !== 1 ? 's' : ''}`);
      else toast.error(`${ok} synced, ${fail} failed`);
      setSelected(new Set());
      setSyncResults({});
      onRefresh();
    } catch (err: any) {
      toast.error(getApiError(err, 'Sync failed'));
    } finally {
      setSyncing(false);
    }
  }, [table, direction, source, onRefresh]);

  const counts = {
    all: detail.records.length,
    dev_only: detail.records.filter((r) => r.status === 'dev_only').length,
    different: detail.records.filter((r) => r.status === 'different').length,
    prod_only: detail.records.filter((r) => r.status === 'prod_only').length,
    identical: detail.records.filter((r) => r.status === 'identical').length,
  };

  const filterOptions: { value: StatusFilter; label: string; color?: 'warning' | 'error' | 'default' | 'success' }[] = [
    { value: 'all', label: `All (${counts.all})` },
    { value: 'dev_only', label: `${devSideName(source)} (${counts.dev_only})`, color: 'warning' },
    { value: 'different', label: `Different (${counts.different})`, color: 'error' },
    { value: 'prod_only', label: `${prodSideName(source)} (${counts.prod_only})`, color: 'default' },
    { value: 'identical', label: `Identical (${counts.identical})`, color: 'success' },
  ];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton onClick={onBack} size="small"><BackIcon /></IconButton>
          <Typography variant="body2" color="text.secondary">Config Sync /</Typography>
          <Typography variant="body2" fontWeight={600}>{TABLE_LABELS[table]}</Typography>
        </Box>
        {/* Israel is read-only to us: there is no direction to choose, and
            offering one suggests records could be pushed back. */}
        {source === 'il' ? (
          <Typography variant="caption" color="text.secondary">Israel → UK</Typography>
        ) : (
          <ToggleButtonGroup
            value={direction}
            exclusive
            size="small"
            onChange={(_, val) => { if (val) onDirectionChange(val); }}
          >
            <ToggleButton value="dev_to_prod">Dev → Prod</ToggleButton>
            <ToggleButton value="prod_to_dev">Prod → Dev</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
          {filterOptions.map((opt) => (
            <Chip
              key={opt.value}
              label={opt.label}
              color={statusFilter === opt.value ? (opt.color || 'primary') : 'default'}
              variant={statusFilter === opt.value ? 'filled' : 'outlined'}
              onClick={() => setStatusFilter(opt.value)}
              size="small"
              clickable
            />
          ))}

          {/* Israeli users only. Both of these decide whether a person can be
              synced here at all, so they are filters rather than just
              columns: pick a set, then select-all takes exactly that set. */}
          {ilUsers && (
            <>
              <Box sx={{ width: 1, height: 20, bgcolor: 'divider', mx: 0.5 }} />
              {(['all', 'active', 'inactive'] as const).map((v) => (
                <Chip
                  key={v}
                  label={v === 'all' ? 'Any state'
                    : v === 'active' ? 'Active in IL' : 'Inactive in IL'}
                  size="small"
                  clickable
                  color={activeFilter === v ? 'primary' : 'default'}
                  variant={activeFilter === v ? 'filled' : 'outlined'}
                  onClick={() => setActiveFilter(v)}
                />
              ))}
              <Box sx={{ width: 1, height: 20, bgcolor: 'divider', mx: 0.5 }} />
              {dataFlagsPresent.map((flag) => (
                <Chip
                  key={flag}
                  label={`data=${flag}`}
                  size="small"
                  clickable
                  color={dataFilter.has(flag) ? 'secondary' : 'default'}
                  variant={dataFilter.has(flag) ? 'filled' : 'outlined'}
                  onClick={() => setDataFilter((prev) => {
                    const next = new Set(prev);
                    next.has(flag) ? next.delete(flag) : next.add(flag);
                    return next;
                  })}
                />
              ))}
              <Typography variant="caption" color="text.secondary">
                {dataFilter.size === 0
                  ? 'every data flag'
                  : `${filtered.length} shown`}
              </Typography>
            </>
          )}
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          {selected.size > 0 && (
            <Button
              variant="contained"
              size="small"
              startIcon={syncing ? <CircularProgress size={14} color="inherit" /> : <SyncIcon />}
              onClick={() => doSync(detail.records.filter((r) => selected.has(r.natural_key)))}
              disabled={syncing}
            >
              Sync Selected ({selected.size})
            </Button>
          )}
          <IconButton onClick={onRefresh} disabled={loading} size="small">
            {loading ? <CircularProgress size={16} /> : <RefreshIcon />}
          </IconButton>
        </Box>
      </Box>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.50' }}>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  indeterminate={selected.size > 0 && !allSyncableSelected}
                  checked={allSyncableSelected}
                  onChange={toggleAll}
                  disabled={syncable.length === 0}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              {ilUsers && <TableCell sx={{ fontWeight: 600 }}>In Israel</TableCell>}
              {ilUsers && <TableCell sx={{ fontWeight: 600 }}>Data</TableCell>}
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Changed Fields</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={ilUsers ? 7 : 5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No records
                </TableCell>
              </TableRow>
            )}
            {filtered.map((record) => {
              const syncResult = syncResults[record.natural_key];
              const isSyncable = canSync(record.status, direction);
              const displayKey = record.natural_key.length > 60
                ? record.natural_key.slice(0, 60) + '…'
                : record.natural_key;

              return (
                <React.Fragment key={record.natural_key}>
                  <TableRow
                    hover
                    sx={{
                      backgroundColor: syncResult?.success
                        ? 'rgba(76,175,80,0.05)'
                        : syncResult && !syncResult.success
                        ? 'rgba(244,67,54,0.05)'
                        : undefined,
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selected.has(record.natural_key)}
                        onChange={() => toggleOne(record.natural_key)}
                        disabled={!isSyncable}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={record.natural_key.length > 60 ? record.natural_key : ''}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {displayKey}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    {ilUsers && (
                      <TableCell>
                        {record.dev_record ? (
                          <Chip
                            size="small"
                            label={isActiveInIsrael(record) ? 'Active' : 'Inactive'}
                            color={isActiveInIsrael(record) ? 'success' : 'default'}
                            variant={isActiveInIsrael(record) ? 'filled' : 'outlined'}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                    )}
                    {ilUsers && (
                      <TableCell>
                        {record.dev_record ? (
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                            {dataFlagOf(record) || '—'}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <Chip
                        label={sideLabel(record.status, source)}
                        color={STATUS_CHIP[record.status]?.color}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {record.diff_fields.length > 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                          {record.diff_fields.join(', ')}
                        </Typography>
                      ) : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {syncResult && (
                        <Chip
                          label={syncResult.success ? syncResult.action : 'failed'}
                          color={syncResult.success ? 'success' : 'error'}
                          size="small"
                          sx={{ mr: 1 }}
                        />
                      )}
                      {isSyncable && (
                        <Button
                          size="small"
                          startIcon={syncing ? <CircularProgress size={12} /> : <SyncIcon />}
                          onClick={() => doSync([record])}
                          disabled={syncing}
                        >
                          Sync
                        </Button>
                      )}
                      {record.diff_fields.length > 0 && (
                        <RecordExpander record={record} source={source} />
                      )}
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────

const ConfigSyncPage: React.FC = () => {
  const [summary, setSummary] = useState<DiffSummaryResponse | null>(null);
  const [selectedTable, setSelectedTable] = useState<ConfigTable | null>(null);
  const [detail, setDetail] = useState<TableDiffResponse | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [direction, setDirection] = useState<SyncDirection>('dev_to_prod');
  // Which system records are read FROM. Defaults to the peer UK environment,
  // which is what this screen did before Israel existed as a source.
  const [source, setSource] = useState<ConfigSource>('uk');
  // The first diff must wait for /sources to say which source this tenant
  // actually has. Firing on the 'uk' default meant the Israeli tenant asked
  // for a UK diff it can never serve - a 503 on every page load, and an error
  // flash before the real source arrived.
  const [sourceResolved, setSourceResolved] = useState(false);
  const [sources, setSources] = useState<SyncSource[]>([]);
  const [initializingMapping, setInitializingMapping] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileReport, setReconcileReport] = useState<IlReconcileReport | null>(null);
  const [mappingReport, setMappingReport] = useState<InitializeMappingResponse | null>(null);

  const handleInitializeMapping = useCallback(async () => {
    setInitializingMapping(true);
    try {
      const report = await configSyncService.initializeMapping();
      setMappingReport(report);
      toast.success('ID mapping initialized');
    } catch (err: any) {
      toast.error(getApiError(err, 'Failed to initialize ID mapping'));
    } finally {
      setInitializingMapping(false);
    }
  }, []);

  // Deactivating Israeli users who have left the feed. Two presses on purpose:
  // the first reports, the second applies what was reported - and the pass
  // itself refuses rather than acting when the numbers look wrong, which is
  // the case worth reading before pressing anything.
  const runReconcile = useCallback(async (dryRun: boolean) => {
    setReconciling(true);
    try {
      const report = await configSyncService.reconcileIlUsers(dryRun);
      setReconcileReport(report);
      if (report.refused) {
        toast.error(report.refused);
      } else if (dryRun) {
        toast.success(
          report.to_deactivate.length === 0
            ? 'Nothing to deactivate'
            : `${report.to_deactivate.length} account(s) would be deactivated`,
        );
      } else {
        toast.success(`Deactivated ${report.deactivated} account(s)`);
      }
    } catch (err: any) {
      toast.error(getApiError(err, 'Reconcile failed'));
    } finally {
      setReconciling(false);
    }
  }, []);

  const loadSummary = useCallback(async () => {
    setLoadingSummary(true);
    setError(null);
    try {
      const data = await configSyncService.getDiffSummary(source);
      setSummary(data);
    } catch (err: any) {
      setError(getApiError(err, 'Failed to load diff summary'));
    } finally {
      setLoadingSummary(false);
    }
  }, [source]);

  const loadDetail = useCallback(async (table: ConfigTable) => {
    setLoadingDetail(true);
    setError(null);
    try {
      const data = await configSyncService.getTableDiff(table, source);
      setDetail(data);
    } catch (err: any) {
      setError(getApiError(err, 'An error occurred') || `Failed to load diff for ${table}`);
    } finally {
      setLoadingDetail(false);
    }
  }, [source]);

  useEffect(() => {
    // Ask which sources this tenant may use. A UK tenant gets one and the
    // picker stays hidden; the Israeli tenant gets two.
    configSyncService.getSources()
      .then((d) => {
        const list = d.sources || [];
        setSources(list);
        // Start on whatever this tenant actually has. The Israeli tenant has
        // no UK dev counterpart, so defaulting to 'uk' showed it an error
        // ("Dev sync credentials not configured for tenant 'il'") instead of
        // the tables it can sync.
        const next = d.default || list[0]?.id;
        if (next) {
          setSource(next);
          if (next === 'il') setDirection('dev_to_prod');
        }
      })
      .catch(() => setSources([]))
      // Resolved either way: if the list cannot be fetched, fall back to the
      // old behaviour rather than leaving the page blank forever.
      .finally(() => setSourceResolved(true));
  }, []);

  useEffect(() => {
    if (!sourceResolved) return;
    loadSummary();
  }, [loadSummary, sourceResolved]);

  const switchSource = (next: ConfigSource) => {
    if (next === source) return;
    setSource(next);
    // Israel is one-way, so a prod_to_dev selection carried over from the
    // other tab would send the operator to an endpoint that refuses it.
    if (next === 'il') setDirection('dev_to_prod');
    // Drop anything open from the other source: a table list and a diff from
    // different systems must never be on screen together.
    setSelectedTable(null);
    setDetail(null);
    setError(null);
  };

  const handleDrillIn = (table: ConfigTable) => {
    setSelectedTable(table);
    setDetail(null);
    loadDetail(table);
  };

  const handleBack = () => {
    setSelectedTable(null);
    setDetail(null);
    setError(null);
    loadSummary();
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      {/* One tab per source. Only shown when the tenant has more than one -
          a single tab is a label pretending to be a control. */}
      {sources.length > 1 && (
        <Tabs
          value={source}
          onChange={(_, v) => switchSource(v as ConfigSource)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          {sources.map((src) => (
            <Tab
              key={src.id}
              value={src.id}
              label={src.id === 'il' ? 'Israel → UK' : 'UK Dev ↔ Prod'}
              disabled={!src.available}
              title={src.detail || undefined}
            />
          ))}
        </Tabs>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {(SHOW_INIT_MAPPING || source === 'il') && (
        <Box display="flex" justifyContent="flex-end" gap={1} mb={2}>
          {source === 'il' && (
            <Button
              variant="outlined"
              size="small"
              color="warning"
              startIcon={reconciling ? <CircularProgress size={14} /> : undefined}
              onClick={() => runReconcile(true)}
              disabled={reconciling}
            >
              Check for users who left Israel
            </Button>
          )}
          {SHOW_INIT_MAPPING && (
            <Button
              variant="outlined"
              size="small"
              startIcon={initializingMapping ? <CircularProgress size={14} /> : <LinkIcon />}
              onClick={handleInitializeMapping}
              disabled={initializingMapping}
            >
              Initialize ID Mapping
            </Button>
          )}
        </Box>
      )}

      <Dialog
        open={!!reconcileReport}
        onClose={() => setReconcileReport(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Users no longer in the Israeli feed</DialogTitle>
        <DialogContent dividers>
          {reconcileReport?.refused ? (
            <Alert severity="error">{reconcileReport.refused}</Alert>
          ) : reconcileReport && reconcileReport.to_deactivate.length === 0 ? (
            <Typography variant="body2">
              Every Israeli account here is still in the feed. Nothing to do.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {reconcileReport?.deactivated
                  ? `Deactivated ${reconcileReport.deactivated} account(s).`
                  : 'These accounts would be deactivated. They are not deleted, '
                    + 'and each change can be rolled back from the snapshots list.'}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell align="right">Israeli id</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reconcileReport?.to_deactivate.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.email}</TableCell>
                      <TableCell align="right">{row.il_user_id ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReconcileReport(null)}>Close</Button>
          {reconcileReport
            && !reconcileReport.refused
            && reconcileReport.dry_run
            && reconcileReport.to_deactivate.length > 0 && (
            <Button
              color="warning"
              variant="contained"
              disabled={reconciling}
              onClick={() => runReconcile(false)}
            >
              Deactivate them
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={!!mappingReport} onClose={() => setMappingReport(null)} maxWidth="sm" fullWidth>
        <DialogTitle>ID Mapping Report</DialogTitle>
        <DialogContent dividers>
          {mappingReport && mappingReport.tables.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Table</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Mapped</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>{devSideName(source)}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>{prodSideName(source)}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Ambiguous</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mappingReport.tables.map((t) => (
                  <TableRow key={t.table}>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {TABLE_LABELS[t.table as ConfigTable] ?? t.table}
                    </TableCell>
                    <TableCell align="center">{t.mapped}</TableCell>
                    <TableCell align="center">{t.dev_only}</TableCell>
                    <TableCell align="center">{t.prod_only}</TableCell>
                    <TableCell align="center">
                      {t.ambiguous > 0 ? (
                        <Chip label={t.ambiguous} size="small" color="warning" />
                      ) : 0}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" color="text.secondary">No mapping data returned.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMappingReport(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {!selectedTable ? (
        loadingSummary && !summary ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        ) : summary ? (
          <SummaryView
            summary={summary}
            onDrillIn={handleDrillIn}
            onRefresh={loadSummary}
            loading={loadingSummary}
            direction={direction}
            onDirectionChange={setDirection}
            source={source}
          />
        ) : null
      ) : (
        loadingDetail && !detail ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        ) : detail ? (
          <TableDetailView
            table={selectedTable}
            detail={detail}
            onBack={handleBack}
            onRefresh={() => loadDetail(selectedTable)}
            loading={loadingDetail}
            direction={direction}
            onDirectionChange={setDirection}
            source={source}
            defaultDataFlags={
              sources.find((x) => x.id === source)?.default_data_flags || []
            }
          />
        ) : null
      )}
    </Box>
  );
};

export default ConfigSyncPage;
